import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Paper, Button, Divider, Alert, Skeleton, Tooltip,
} from '@mui/material';
import { Add, PointOfSale, OpenInNew, ExitToApp as WithdrawnIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { AdmissionCycle, AdmissionApplication, ConcessionRequest } from '../../../types/admissions';
import { FeeInvoiceKind, FEE_INVOICE_KIND_LABEL, FEE_INVOICE_STATUS_META } from '../../../types/fees';
import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';
import { listApplicationInvoices, ApplicationChallan } from '../../../services/feeInvoiceService';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { useAuth } from '../../../context/AuthContext';
import { useApprovalRule } from '../../../hooks/useApprovalRule';
import dateTimeUtils from '../../../utils/dateTimeUtils';
import { formatChallanNumber } from '../../../utils/challanNumber';
import { applicableAdmissionFeeHeads, sumFeeHeads, concessionAmountLabel } from '../../../utils/admissionFees';
import { ToneChip, ChipTone } from '../../common/StatusChip';
import { GroupLabel } from '../tabLayout';
import ConcessionRequestDialog, { concessionTypeLabel } from '../dialogs/ConcessionRequestDialog';
import RecordFeePaymentDialog from '../../fees/RecordFeePaymentDialog';
import InvoiceDetailDialog from '../../fees/InvoiceDetailDialog';
import ConfirmationDialog, { ConfirmFacts } from '../../common/ConfirmationDialog';
import { useCurrencyFormat } from '../../../hooks/useCurrencyFormat';

/**
 * Fees tab — the applicant's challans (application fee, admission fee) and
 * concession requests.
 *
 * Challans are loaded from `app.feeInvoiceIds` (the application keeps ids
 * only; status and totals are always read fresh). Paying or waiving a
 * challan moves the application on by itself — no status change needed.
 */

interface Props {
  app: AdmissionApplication;
  cycle: AdmissionCycle;
  onChanged: () => Promise<void> | void;
}

const CONCESSION_STATUS: Record<ConcessionRequest['status'], { label: string; tone: ChipTone }> = {
  pending:  { label: 'Pending',  tone: 'warning' },
  approved: { label: 'Approved', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'neutral' },
};

/** What happens once each kind of challan is settled. */
const SETTLES_TO: Record<string, string> = {
  application_fee: 'Moves to Under review once paid or waived.',
  admission_fee: 'Seat is confirmed once paid or waived.',
};

const idOf = (v: string | { _id: string } | null | undefined) => (v && typeof v === 'object' ? v._id : v) || '';

const CandidateFeesTab: React.FC<Props> = ({ app, cycle, onChanged }) => {
  const money = useCurrencyFormat();
  const { enqueueSnackbar } = useSnackbar();
  const { canRead, canWrite } = useModulePermissions();
  const { decodedToken } = useAuth();
  const { ownBlocked } = useApprovalRule();
  const [invoices, setInvoices] = useState<ApplicationChallan[] | null>(null);
  const [outstanding, setOutstanding] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [concessionOpen, setConcessionOpen] = useState(false);
  const [busyConcessionIdx, setBusyConcessionIdx] = useState<number | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [openInvoiceId, setOpenInvoiceId] = useState<string | null>(null);

  const canSeeFees = canRead('fees');
  const canCollect = canWrite('fees');
  const canApprove = canWrite('admissions_concessions_approve');
  const personId = idOf(app.personId as any);
  const classId = idOf(app.classId as any);

  useEffect(() => {
    let cancelled = false;
    if (!(app.feeInvoiceIds || []).length || !canSeeFees) { setInvoices([]); setOutstanding(0); return; }
    setInvoices(null);
    setLoadError(null);
    // One request: the challans (open first, newest first) and the total owed.
    listApplicationInvoices(app._id)
      .then(r => { if (!cancelled) { setInvoices(r.invoices); setOutstanding(r.totals.outstanding); } })
      .catch((err: any) => {
        if (cancelled) return;
        setInvoices([]);
        setLoadError(err?.message || 'Could not load the challans');
      });
    return () => { cancelled = true; };
  }, [app._id, app.feeInvoiceIds, canSeeFees]);

  const sorted = invoices || [];

  const admissionFeeTotal = useMemo(
    () => sumFeeHeads(applicableAdmissionFeeHeads(cycle.admissionFeeHeads, classId)),
    [cycle.admissionFeeHeads, classId],
  );

  // Approving changes what the family pays — confirmed first, as is rejecting.
  const [confirmConcession, setConfirmConcession] = useState<{ idx: number; decision: 'approved' | 'rejected' } | null>(null);

  const decide = async (idx: number, decision: 'approved' | 'rejected') => {
    setConfirmConcession(null);
    setBusyConcessionIdx(idx);
    try {
      await admissionApplicationService.decideConcessionRequest(app._id, idx, decision);
      enqueueSnackbar(decision === 'approved' ? 'Concession approved' : 'Concession rejected', { variant: 'success' });
      await onChanged();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to update concession', { variant: 'error' });
    } finally {
      setBusyConcessionIdx(null);
    }
  };

  const concessions = (app.concessionRequests || []) as ConcessionRequest[];

  return (
    <>
      <Stack spacing={3}>
        {app.withdrawal && (
          <Alert severity="warning" variant="outlined" icon={<WithdrawnIcon fontSize="small" />} sx={{ borderRadius: 2.5 }}>
            <Typography variant="body2" fontWeight={600}>Application withdrawn</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {dateTimeUtils.formatDateForDisplay(app.withdrawal.at)} · {app.withdrawal.reason}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Unpaid challans were cancelled. Any fee already collected is refunded from Fees.
            </Typography>
          </Alert>
        )}

        {/* ── Challans ───────────────────────────────────────────── */}
        <Box>
          <GroupLabel
            count={sorted.length || undefined}
            action={outstanding > 0 ? (
              <Typography variant="body2" fontWeight={700} color="warning.dark" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {money(outstanding)} due
              </Typography>
            ) : undefined}
          >
            Challans
          </GroupLabel>

          {!canSeeFees && (app.feeInvoiceIds || []).length > 0 ? (
            <EmptyNote>Your role can’t view fee challans. Ask an administrator for Fees access.</EmptyNote>
          ) : invoices === null ? (
            <Stack spacing={1.5}>
              {[0, 1].map(i => <Skeleton key={i} variant="rounded" height={132} sx={{ borderRadius: 2.5 }} />)}
            </Stack>
          ) : loadError ? (
            <Alert severity="error" variant="outlined" sx={{ borderRadius: 2.5 }}>{loadError}</Alert>
          ) : sorted.length === 0 ? (
            <EmptyNote>
              No challans yet. The application-fee challan is issued on submission when the cycle charges one,
              and the admission-fee challan when the parent accepts the offer.
            </EmptyNote>
          ) : (
            <Stack spacing={1.5}>
              {sorted.map(inv => (
                <InvoiceCard
                  key={inv._id}
                  invoice={inv}
                  canCollect={canCollect}
                  onOpen={() => setOpenInvoiceId(inv._id)}
                  onPay={() => setPayOpen(true)}
                />
              ))}
            </Stack>
          )}
        </Box>

        {/* ── Concessions ────────────────────────────────────────── */}
        <Box>
          <GroupLabel
            count={concessions.length || undefined}
            action={canWrite('admissions_concessions') && (
              <Button size="small" startIcon={<Add fontSize="small" />} onClick={() => setConcessionOpen(true)} sx={{ textTransform: 'none' }}>
                Request
              </Button>
            )}
          >
            Concessions
          </GroupLabel>

          {concessions.length === 0 ? (
            <EmptyNote>
              No concessions. Tuition concessions (sibling, staff child, scholarship) start from enrollment;
              admission-fee concessions come off the admission challan.
            </EmptyNote>
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
              {concessions.map((c, idx) => {
                const mine = idOf(c.requestedBy as any) === decodedToken?.userId;
                const canDecide = c.status === 'pending' && canApprove && !ownBlocked(mine);
                const status = CONCESSION_STATUS[c.status];
                return (
                  <Box key={idx} sx={{ px: 2, py: 1.75, borderTop: idx ? 1 : 0, borderColor: 'divider' }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="body2" fontWeight={600}>{concessionTypeLabel(c.type)}</Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {concessionAmountLabel(c)}
                          </Typography>
                          <ToneChip label={status.label} tone={status.tone} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                          {c.carryToFees ? 'Monthly tuition, from enrollment' : 'Admission fee, one-time'}
                          {' · requested '}{dateTimeUtils.formatDateForDisplay(c.requestedAt)}
                          {c.status !== 'pending' && c.approvedAt && <> · {c.status} {dateTimeUtils.formatDateForDisplay(c.approvedAt)}</>}
                        </Typography>
                        {c.reason && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{c.reason}</Typography>
                        )}
                        {c.status === 'pending' && !canDecide && (
                          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                            {mine && canApprove ? 'You requested this — another approver decides it.' : 'Waiting for approval.'}
                          </Typography>
                        )}
                      </Box>
                      {canDecide && (
                        <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
                          <Button size="small" color="inherit" disabled={busyConcessionIdx === idx} onClick={() => setConfirmConcession({ idx, decision: 'rejected' })} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                            Reject
                          </Button>
                          <Button size="small" variant="contained" disableElevation disabled={busyConcessionIdx === idx} onClick={() => setConfirmConcession({ idx, decision: 'approved' })} sx={{ textTransform: 'none' }}>
                            Approve
                          </Button>
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Paper>
          )}
        </Box>
      </Stack>

      {confirmConcession && concessions[confirmConcession.idx] && (() => {
        const c = concessions[confirmConcession.idx];
        const approve = confirmConcession.decision === 'approved';
        return (
          <ConfirmationDialog
            open
            onClose={() => setConfirmConcession(null)}
            onConfirm={() => decide(confirmConcession.idx, confirmConcession.decision)}
            title={approve ? `Approve ${concessionTypeLabel(c.type)} concession?` : 'Reject this concession request?'}
            message={approve
              ? (c.carryToFees ? 'It applies to monthly tuition from enrollment.' : 'It is taken off the one-time admission fee.')
              : 'The family pays the full amount. A new request can be raised later if needed.'}
            confirmText={approve ? 'Approve' : 'Reject'}
            confirmColor={approve ? 'primary' : 'error'}
          >
            <ConfirmFacts items={[
              { label: 'Concession', value: `${concessionTypeLabel(c.type)} · ${concessionAmountLabel(c)}` },
              { label: 'Applies to', value: c.carryToFees ? 'Monthly tuition' : 'Admission fee' },
              ...(c.reason ? [{ label: 'Reason', value: c.reason }] : []),
            ]} />
          </ConfirmationDialog>
        );
      })()}
      {concessionOpen && (
        <ConcessionRequestDialog
          open
          applicationId={app._id}
          admissionFeeTotal={admissionFeeTotal}
          onCancel={() => setConcessionOpen(false)}
          onAdded={async () => { setConcessionOpen(false); await onChanged(); }}
        />
      )}

      <RecordFeePaymentDialog
        open={payOpen}
        personId={payOpen ? personId : null}
        onClose={() => setPayOpen(false)}
        // Stays open on success so the cashier can print the receipt; the
        // drawer refreshes to show the paid challan and the new status.
        onRecorded={async () => { await onChanged(); }}
      />

      <InvoiceDetailDialog
        open={!!openInvoiceId}
        invoiceId={openInvoiceId}
        onClose={async () => { setOpenInvoiceId(null); await onChanged(); }}
      />
    </>
  );
};

// ─── Pieces ─────────────────────────────────────────────────────────────────

const EmptyNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2.5, px: 2.5, py: 2, borderStyle: 'dashed' }}>
    <Typography variant="body2" color="text.secondary">{children}</Typography>
  </Paper>
);

const Row: React.FC<{ label: React.ReactNode; value: React.ReactNode; strong?: boolean; muted?: boolean }> = ({ label, value, strong, muted }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" color={muted ? 'text.secondary' : 'text.primary'} fontWeight={strong ? 700 : 400}>{label}</Typography>
    <Typography variant="body2" color={muted ? 'text.secondary' : 'text.primary'} fontWeight={strong ? 700 : 500} sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
      {value}
    </Typography>
  </Stack>
);

const InvoiceCard: React.FC<{
  invoice: ApplicationChallan;
  canCollect: boolean;
  onOpen: () => void;
  onPay: () => void;
}> = ({ invoice: inv, canCollect, onOpen, onPay }) => {
  const money = useCurrencyFormat();
  const status = FEE_INVOICE_STATUS_META[inv.status];
  const cancelled = inv.status === 'cancelled';
  const open = inv.status === 'issued' || inv.status === 'partial';
  const t = inv.totals;
  const kind = inv.kind ?? 'tuition';

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden', opacity: cancelled ? 0.65 : 1 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ px: 2, pt: 1.75, pb: 1.25 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" fontWeight={700}>{FEE_INVOICE_KIND_LABEL[kind as FeeInvoiceKind] ?? 'Challan'}</Typography>
            <ToneChip label={status.label} tone={status.tone} />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatChallanNumber(inv.invoiceNumber)}
            {cancelled
              ? ` · ${inv.cancellationReason || 'cancelled'}`
              : ` · due ${dateTimeUtils.formatDateForDisplay(inv.dueDate)}`}
          </Typography>
        </Box>
        <Tooltip title="Open challan — print, waive, see payments">
          <Button size="small" color="inherit" endIcon={<OpenInNew sx={{ fontSize: '14px !important' }} />} onClick={onOpen} sx={{ textTransform: 'none', color: 'text.secondary', flexShrink: 0 }}>
            Open
          </Button>
        </Tooltip>
      </Stack>

      {!cancelled && (
        <Stack spacing={0.5} sx={{ px: 2, pb: 1.5 }}>
          {inv.lineItems.map((li, i) => (
            <Row key={i} label={li.categoryName} value={money(li.grossAmount ?? 0)} />
          ))}
          {t.concessions + t.waivers > 0 && <Row muted label="Concession / waiver" value={`− ${money(t.concessions + t.waivers)}`} />}
          {t.paid > 0 && <Row muted label="Paid" value={`− ${money(t.paid)}`} />}
          <Divider sx={{ my: 0.5 }} />
          <Row strong label={open ? 'Due' : 'Balance'} value={money(t.outstanding)} />
        </Stack>
      )}

      {open && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider', bgcolor: 'action.hover' }}
        >
          <Typography variant="caption" color="text.secondary">{SETTLES_TO[kind]}</Typography>
          {canCollect && (
            <Button size="small" variant="contained" disableElevation startIcon={<PointOfSale fontSize="small" />} onClick={onPay} sx={{ textTransform: 'none', flexShrink: 0 }}>
              Record payment
            </Button>
          )}
        </Stack>
      )}
    </Paper>
  );
};

export default CandidateFeesTab;
